// admin-panel/src/pages/Media.jsx
import { useState, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { mediaApi } from '../lib/api';
import { Upload, Trash2, Copy, CheckSquare, Square, X, Image as ImgIcon } from 'lucide-react';
import toast from 'react-hot-toast';

export default function Media() {
  const qc = useQueryClient();
  const fileRef = useRef();
  const [selected, setSelected] = useState([]);
  const [preview, setPreview] = useState(null);

  const { data, isLoading } = useQuery({ queryKey:['admin-media'], queryFn:()=>mediaApi.list({per_page:60}) });
  const list = data?.data || data || [];

  const uploadMut = useMutation({
    mutationFn: (fd) => mediaApi.upload(fd),
    onSuccess: () => { qc.invalidateQueries(['admin-media']); toast.success('Uploaded!'); },
    onError: (e) => toast.error(e.message||'Upload failed'),
  });
  const delMut = useMutation({
    mutationFn: mediaApi.delete,
    onSuccess: () => { qc.invalidateQueries(['admin-media']); toast.success('Deleted'); },
  });
  const bulkDelMut = useMutation({
    mutationFn: mediaApi.bulkDelete,
    onSuccess: () => { qc.invalidateQueries(['admin-media']); setSelected([]); toast.success(`${selected.length} files deleted`); },
  });

  function handleUpload(e) {
    const files = Array.from(e.target.files);
    if (!files.length) return;
    const fd = new FormData();
    files.forEach(f => fd.append('files[]', f));
    uploadMut.mutate(fd);
    e.target.value = '';
  }

  function toggleSelect(id) {
    setSelected(prev => prev.includes(id) ? prev.filter(i=>i!==id) : [...prev, id]);
  }

  function copyUrl(url) { navigator.clipboard.writeText(url); toast.success('URL copied!'); }

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-bold text-white">Media Library</h1>
        <div className="flex gap-3">
          {selected.length > 0 && (
  <button
    onClick={() => {
      if (window.confirm('Delete selected items?')) {
        bulkDelMut.mutate(selected);
      }
    }}
    className="flex items-center gap-2 px-4 py-2.5 text-sm font-medium text-red-400 border border-red-500/30 rounded-xl hover:bg-red-500/10 transition-all"
  >
    <Trash2 size={14}/> Delete ({selected.length})
  </button>
)}
          <label className="flex items-center gap-2 bg-gradient-to-r from-[#8B5E3C] to-[#D4AF37] text-white text-sm font-semibold px-4 py-2.5 rounded-xl hover:opacity-90 transition-all cursor-pointer">
            {uploadMut.isPending ? <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"/> : <><Upload size={16}/> Upload</>}
            <input type="file" accept="image/*,video/*" multiple onChange={handleUpload} className="hidden" ref={fileRef}/>
          </label>
        </div>
      </div>

      {/* Drop zone */}
      <label className="flex flex-col items-center justify-center h-24 border-2 border-dashed border-white/10 rounded-xl cursor-pointer hover:border-[#D4AF37]/30 transition-colors bg-[#151515]">
        <Upload size={20} className="text-[#444] mb-1"/>
        <span className="text-xs text-[#555]">Drag & drop or click to upload images</span>
        <input type="file" accept="image/*" multiple onChange={handleUpload} className="hidden"/>
      </label>

      {isLoading ? (
        <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-8 gap-3">
          {Array(24).fill(0).map((_,i)=><div key={i} className="aspect-square rounded-xl bg-white/5 animate-pulse"/>)}
        </div>
      ) : list.length === 0 ? (
        <div className="text-center py-16 text-[#555]"><ImgIcon size={40} className="mx-auto mb-3 text-[#333]"/><p>No media uploaded yet.</p></div>
      ) : (
        <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-8 gap-3">
          {list.map(m => (
            <div key={m.id} className="group relative aspect-square rounded-xl overflow-hidden bg-[#1A1A1A] border border-white/5 hover:border-[#D4AF37]/20 cursor-pointer transition-all"
              onClick={() => setPreview(m)}>
              <img src={m.file_url} alt={m.alt_text||m.filename} className="w-full h-full object-cover"/>
              <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-1.5">
                <button onClick={e=>{e.stopPropagation();toggleSelect(m.id)}} className="p-1.5 bg-white/10 rounded-lg hover:bg-white/20">
                  {selected.includes(m.id) ? <CheckSquare size={13} className="text-[#D4AF37]"/> : <Square size={13} className="text-white"/>}
                </button>
                <button onClick={e=>{e.stopPropagation();copyUrl(m.file_url)}} className="p-1.5 bg-white/10 rounded-lg hover:bg-white/20"><Copy size={13} className="text-white"/></button>
                <button onClick={e=>{e.stopPropagation();if(window.confirm('Delete?'))delMut.mutate(m.id)}} className="p-1.5 bg-red-500/30 rounded-lg hover:bg-red-500/50"><Trash2 size={13} className="text-red-300"/></button>
              </div>
              {selected.includes(m.id) && <div className="absolute inset-0 ring-2 ring-[#D4AF37] rounded-xl pointer-events-none"/>}
            </div>
          ))}
        </div>
      )}

      {/* Preview Modal */}
      {preview && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4" onClick={()=>setPreview(null)}>
          <div className="bg-[#1A1A1A] border border-white/10 rounded-2xl overflow-hidden max-w-2xl w-full" onClick={e=>e.stopPropagation()}>
            <div className="flex items-center justify-between p-4 border-b border-white/5">
              <span className="text-sm text-white font-medium truncate">{preview.original_name}</span>
              <button onClick={()=>setPreview(null)}><X size={18} className="text-[#888] hover:text-white"/></button>
            </div>
            <img src={preview.file_url} className="w-full max-h-96 object-contain bg-[#121212]"/>
            <div className="p-4 flex gap-3">
              <button onClick={()=>copyUrl(preview.file_url)} className="flex items-center gap-2 px-4 py-2 bg-[#D4AF37]/10 border border-[#D4AF37]/30 text-[#D4AF37] rounded-xl text-sm hover:bg-[#D4AF37]/20 transition-all"><Copy size={14}/> Copy URL</button>
              <button onClick={()=>{if(window.confirm('Delete?')){delMut.mutate(preview.id);setPreview(null)}}} className="flex items-center gap-2 px-4 py-2 bg-red-500/10 border border-red-500/30 text-red-400 rounded-xl text-sm hover:bg-red-500/20 transition-all"><Trash2 size={14}/> Delete</button>
              <span className="text-xs text-[#555] ml-auto self-center">{preview.width}×{preview.height} · {(preview.file_size/1024).toFixed(0)}KB</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
